<template>
  <div id="banner_wrapper">
    <NotificationBanner v-if="showNotificationBanner && !isPublicRoute && isUserAuthenticated" />
    <DynamicBanner
      v-if="showDynamicBanner"
      :source="bannerComponentTypes.AUTOPLAY_TASK"
      @cross-click="handleCrossClick"
    >
      <AutoplayTaskHeader v-if="isAutoplayTask" />
      <CallCampaignBanner v-if="isCallCampaign" />
    </DynamicBanner>

    <component
      v-if="showConfirmModal && getConfirmationModal"
      :is="getConfirmationModal"
      v-model:visible="showConfirmModal"
      @confirm="handleConfirmClose"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watchEffect } from "vue";
import { useRoute } from "vue-router";
import { bannerComponentTypes } from "@/src/constants/bannerConstants.js";
import store from "@/src/store/index.js";
import DynamicBanner from "@/src/views/layout/DynamicBanner.vue";
import AutoplayExitModal from "@/src/components/autoplay_components/AutoplayExitModal.vue";
import AutoplayTaskHeader from "@/src/components/autoplay_components/AutoplayTaskHeader.vue";
import CallCampaignBanner from "@/src/views/calls/call_campaigns/CallCampaignBanner.vue";
import NotificationBanner from "@/src/views/layout/NotificationBanner.vue";
import CallCampaignPauseModal from "@/src/components/telephony_components/campaigns/CallCampaignPauseModal.vue";
import { callCampaignStatuses } from "@/src/constants/callConstants.js";
import { useAuthStore } from "@/src/composables/useAuthStore.js";

const route = useRoute();
const authStore = useAuthStore();

const showConfirmModal = ref(false);

const isUserAuthenticated = computed(() => authStore.isAuthenticated.value);
const isPublicRoute = computed(() => route.meta.public === true);

const showNotificationBanner = computed(() => {
  return (
    store.getters["dynamicBanner/getNotificationBannerStatus"] &&
    !store.getters["dynamicBanner/getShowBanner"]
  );
});

const showDynamicBanner = computed(
  () => store.getters["dynamicBanner/getShowBanner"],
);

const bannerComponentType = computed(
  () => store.getters["dynamicBanner/getBannerComponentType"],
);

const isCallCampaign = computed(
  () =>
    bannerComponentType.value === bannerComponentTypes.CALL_CAMPAIGNS &&
    (store.getters["callsCampaign/isCurrentCampaign"] ||
      store.getters["callsCampaign/isCampaignPageOpen"]),
);

const isAutoplayTask = computed(
  () => bannerComponentType.value === bannerComponentTypes.AUTOPLAY_TASK,
);

const handleAutoplayClose = async () => {
  try {
    await store.dispatch("tasks/resetAutoPlayState");
    await store.dispatch("dynamicBanner/updateShowBanner", false);
  } catch (error) {
    console.error("An error occurred while handling autoplay close: ", error);
  }
};

const getConfirmationModal = computed(() => {
  if (bannerComponentType.value === bannerComponentTypes.AUTOPLAY_TASK) {
    return AutoplayExitModal;
  } else if (
    bannerComponentType.value === bannerComponentTypes.CALL_CAMPAIGNS
  ) {
    return CallCampaignPauseModal;
  }
  return null;
});

const handleCrossClick = () => {
  if (getConfirmationModal.value) {
    showConfirmModal.value = true;
  } else {
    handleConfirmClose();
  }
};

const handleConfirmClose = async () => {
  try {
    if (bannerComponentType.value === bannerComponentTypes.AUTOPLAY_TASK) {
      await handleAutoplayClose();
    } else {
      await store.dispatch("dynamicBanner/updateShowBanner", false);
    }
    showConfirmModal.value = false;
  } catch (error) {
    console.error(`Error closing banner:`, error);
  }
};

watchEffect(() =>
  document.documentElement.style.setProperty(
    "--banner-height",
    showDynamicBanner.value
      ? "64px"
      : showNotificationBanner.value
        ? "50px"
        : "0px",
  ),
);

onMounted(() => {
  if (!isPublicRoute.value && isUserAuthenticated.value) {
    store.dispatch("tasks/initStore");
  }
});
</script>

<style>
.banner_hover_action {
  padding: 10px 16px;
  border-radius: 8px;
  transition: background-color 0.4s ease-in-out;
  color: var(--foreground-rgb);
  font-family: "Proxima Nova", sans-serif;
  font-size: 16px;
  font-weight: 600;
}

.banner_hover_action:hover {
  background-color: var(--white-opacity-8);
}
</style>
